import { useState, useEffect } from 'react';
import {
  fetchMarketplaceReports,
  reviewReport,
  dismissReport,
  getReportStats,
  type MarketplaceReportFilters,
} from '@/api/marketplace-reports.api';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, CheckCircle, XCircle, Clock, TrendingUp } from 'lucide-react';

export default function MarketplaceReports() {
  const [reports, setReports] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<MarketplaceReportFilters>({
    status: 'pending',
    limit: 20,
  });
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewFormData, setReviewFormData] = useState({
    resolution: '',
    actionTaken: 'none' as any,
    adminNotes: '',
  });

  // Fetch reports
  const loadReports = async () => {
    try {
      setLoading(true);
      const data = await fetchMarketplaceReports(filters);
      setReports(data.data || []);
    } catch (error) {
      console.error('Failed to load reports:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const loadStats = async () => {
    try {
      const data = await getReportStats();
      setStats(data.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  useEffect(() => {
    loadReports();
    loadStats();
  }, [filters]);

  const handleReviewReport = async () => {
    if (!selectedReport) return;

    try {
      await reviewReport(selectedReport._id, reviewFormData);
      setReviewDialogOpen(false);
      setSelectedReport(null);
      setReviewFormData({
        resolution: '',
        actionTaken: 'none',
        adminNotes: '',
      });
      loadReports();
      loadStats();
    } catch (error) {
      console.error('Failed to review report:', error);
    }
  };

  const handleDismissReport = async (reportId: string) => {
    try {
      await dismissReport(reportId, 'Invalid or duplicate report');
      loadReports();
      loadStats();
    } catch (error) {
      console.error('Failed to dismiss report:', error);
    }
  };

  const openReviewDialog = (report: any) => {
    setSelectedReport(report);
    setReviewDialogOpen(true);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: any = {
      pending: { variant: 'secondary', icon: Clock, color: 'text-yellow-600' },
      reviewing: { variant: 'default', icon: AlertCircle, color: 'text-blue-600' },
      resolved: { variant: 'default', icon: CheckCircle, color: 'text-green-600' },
      dismissed: { variant: 'outline', icon: XCircle, color: 'text-gray-600' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className={`w-3 h-3 ${config.color}`} />
        {status}
      </Badge>
    );
  };

  const getReportTypeBadge = (type: string) => {
    const colors: any = {
      listing: 'bg-blue-100 text-blue-800',
      user: 'bg-purple-100 text-purple-800',
      review: 'bg-green-100 text-green-800',
      offer: 'bg-orange-100 text-orange-800',
    };

    return (
      <Badge className={colors[type] || ''}>
        {type}
      </Badge>
    );
  };

  const getReasonBadge = (reason: string) => {
    const colors: any = {
      spam: 'bg-gray-100 text-gray-800',
      scam: 'bg-red-100 text-red-800',
      inappropriate: 'bg-yellow-100 text-yellow-800',
      harassment: 'bg-red-100 text-red-800',
      fake: 'bg-orange-100 text-orange-800',
      counterfeit: 'bg-red-100 text-red-800',
      prohibited: 'bg-red-100 text-red-800',
      other: 'bg-gray-100 text-gray-800',
    };

    return (
      <Badge className={colors[reason] || ''} variant="outline">
        {reason}
      </Badge>
    );
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Marketplace Reports</h1>
          <p className="text-muted-foreground">
            Review and manage reports from the marketplace
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.pendingCount || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Reviewing</CardTitle>
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.reviewingCount || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Resolved</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.resolvedCount || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Reports</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalReports || 0}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Filter reports by status, type, and reason</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-4">
          <Select
            value={filters.status || 'all'}
            onValueChange={(value) =>
              setFilters({ ...filters, status: value === 'all' ? undefined : value as any })
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="reviewing">Reviewing</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="dismissed">Dismissed</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.reportType || 'all'}
            onValueChange={(value) =>
              setFilters({ ...filters, reportType: value === 'all' ? undefined : value as any })
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Report Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="listing">Listing</SelectItem>
              <SelectItem value="user">User</SelectItem>
              <SelectItem value="review">Review</SelectItem>
              <SelectItem value="offer">Offer</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.reason || 'all'}
            onValueChange={(value) =>
              setFilters({ ...filters, reason: value === 'all' ? undefined : value as any })
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Reason" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Reasons</SelectItem>
              <SelectItem value="spam">Spam</SelectItem>
              <SelectItem value="scam">Scam</SelectItem>
              <SelectItem value="inappropriate">Inappropriate</SelectItem>
              <SelectItem value="harassment">Harassment</SelectItem>
              <SelectItem value="fake">Fake</SelectItem>
              <SelectItem value="counterfeit">Counterfeit</SelectItem>
              <SelectItem value="prohibited">Prohibited</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Reports Table */}
      <Card>
        <CardHeader>
          <CardTitle>Reports</CardTitle>
          <CardDescription>
            {reports.length} report(s) found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No reports found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Reporter</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map((report) => (
                  <TableRow key={report._id}>
                    <TableCell>{getReportTypeBadge(report.reportType)}</TableCell>
                    <TableCell>{getReasonBadge(report.reason)}</TableCell>
                    <TableCell>
                      <div className="font-medium">
                        {report.reporterId?.username || 'Unknown'}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-md truncate">
                      {report.description}
                    </TableCell>
                    <TableCell>{getStatusBadge(report.status)}</TableCell>
                    <TableCell>
                      {new Date(report.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {report.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => openReviewDialog(report)}
                            >
                              Review
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDismissReport(report._id)}
                            >
                              Dismiss
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="sm:max-w-[525px]">
          <DialogHeader>
            <DialogTitle>Resolve Report</DialogTitle>
            <DialogDescription>
              Document what action was taken and close this report. The reporter will be notified.
            </DialogDescription>
          </DialogHeader>

          {selectedReport && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-muted-foreground">Type</Label>
                  <div>{selectedReport.reportType}</div>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Reason</Label>
                  <div>{selectedReport.reason}</div>
                </div>
              </div>

              <div>
                <Label className="text-sm text-muted-foreground">Description</Label>
                <div className="mt-1 text-sm">{selectedReport.description}</div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="actionTaken">What action was taken?</Label>
                <Select
                  value={reviewFormData.actionTaken}
                  onValueChange={(value) =>
                    setReviewFormData({ ...reviewFormData, actionTaken: value as any })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No action needed</SelectItem>
                    <SelectItem value="listing_removed">Listing was removed</SelectItem>
                    <SelectItem value="user_suspended">User was suspended</SelectItem>
                    <SelectItem value="user_banned">User was banned</SelectItem>
                    <SelectItem value="warning_sent">Warning was sent</SelectItem>
                    <SelectItem value="listing_edited">Listing was edited</SelectItem>
                    <SelectItem value="transaction_cancelled">Transaction was cancelled</SelectItem>
                    <SelectItem value="offer_declined">Offer was declined</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="resolution">Resolution</Label>
                <Textarea
                  id="resolution"
                  placeholder="Explain how this report was resolved..."
                  value={reviewFormData.resolution}
                  onChange={(e) =>
                    setReviewFormData({ ...reviewFormData, resolution: e.target.value })
                  }
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="adminNotes">Admin Notes (Optional)</Label>
                <Textarea
                  id="adminNotes"
                  placeholder="Internal notes for admin reference..."
                  value={reviewFormData.adminNotes}
                  onChange={(e) =>
                    setReviewFormData({ ...reviewFormData, adminNotes: e.target.value })
                  }
                  rows={2}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleReviewReport}>
              Resolve Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
